import {
  Component,
  createContext,
  createEffect,
  createSignal,
  JSX,
  useContext,
} from 'solid-js';
import { makePersisted } from '@solid-primitives/storage';
import { asError } from '@trimble/logging';
import { useLogger } from '@trimble/solidjs-utils';
import { changeLanguage } from '@trimble/i18n';
import { useApiServices } from './ApiServiceProvider';
import { PERSISTENCE_KEY } from './constants/make-persisted-constants';

const DEFAULT_LOCALE = 'en';

/**
 * Get the browser's preferred language as a short locale code.
 */
function getBrowserLocale(): string {
  const browserLang = navigator.language || DEFAULT_LOCALE;
  // Extract the primary language code (e.g., 'en' from 'en-US')
  return browserLang.split('-')[0];
}

/**
 * Convert a Trimble Connect language code to an i18next locale code.
 * TC uses full locale codes (e.g., 'en-us'), but i18next uses short codes (e.g., 'en').
 * Special cases for Chinese variants are handled explicitly.
 * Falls back to 'en' if the language is not supported.
 */
function mapTcLanguageToLocale(tcLanguage: string | undefined): string {
  if (!tcLanguage) return DEFAULT_LOCALE;

  const lang = tcLanguage.toLowerCase();

  // Handle Chinese variants explicitly
  if (lang === 'zh-hant' || lang === 'zh-tw') return 'zh-Hant';
  if (lang === 'zh-hans' || lang === 'zh-cn') return 'zh-Hans';

  // For other languages, extract the primary language code (e.g., 'en' from 'en-us')
  const primaryLang = lang.split('-')[0];
  return primaryLang || DEFAULT_LOCALE;
}

interface LocaleContextValue {
  locale: string;
}

const LocaleContext = createContext<LocaleContextValue>();

export interface LocaleProviderProps {
  children?: JSX.Element;
}

export const LocaleProvider: Component<LocaleProviderProps> = (props) => {
  const loggerContext = useLogger();
  const logger = loggerContext.getLogger('LocaleProvider');
  const apiServices = useApiServices();

  // Persisted locale - uses cached value from localStorage on first load
  // Falls back to browser locale or 'en' if nothing is cached
  const [cachedLocale, setCachedLocale] = makePersisted(
    // eslint-disable-next-line solid/reactivity
    createSignal<string>(getBrowserLocale()),
    {
      name: PERSISTENCE_KEY.Locale,
    }
  );

  // Apply the cached locale immediately on mount (synchronous)
  // This prevents visual flash by using the cached value right away
  const initialLocale = cachedLocale();
  changeLanguage(initialLocale).catch(() => {
    // If the cached locale is invalid, fall back to 'en'
    changeLanguage(DEFAULT_LOCALE);
  });

  // Query for current user to get their language preference
  const currentUserQuery = apiServices.tcTcpsApi.getCurrentUser();

  // Verify locale against user's actual preference when query completes
  createEffect(() => {
    if (currentUserQuery.isSuccess && currentUserQuery.data) {
      const userLanguage = currentUserQuery.data.language;
      const userLocale = mapTcLanguageToLocale(userLanguage);

      // Only reload if the locale has changed
      if (userLocale !== cachedLocale()) {
        logger.info('User locale changed, updating and reloading', {
          cachedLocale: cachedLocale(),
          userLocale,
        });
        setCachedLocale(userLocale);
        // Reload the app to apply the new locale
        window.location.reload();
      }
    } else if (currentUserQuery.isError) {
      logger.error(
        'Error fetching user details, keeping cached locale',
        asError(currentUserQuery.error)
      );
      // Keep using the cached locale - no need to do anything
    }
  });

  return (
    <LocaleContext.Provider value={{ locale: cachedLocale() }}>
      {props.children}
    </LocaleContext.Provider>
  );
};

export const useLocale = (): LocaleContextValue => {
  const context = useContext(LocaleContext);
  if (context === undefined) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }

  return context;
};
